import {
    AST,
    ProgDecl,
    VarExpr,
    MakeStmt,
    ProgCallStmt,
    BuiltInCommand,
    VarDecl,
    IfElseStmt,
    RepeatStmt,
    WhileStmt,
    SpecialBuiltInCommand,
} from "./ir/ast";
import { ASTVisitor } from "./ASTVisitor";
import { Token } from "antlr4ts";
import { SemanticError, SemanticErrorCause } from "./semanticError";

class Scope {
    private definedVariables: Map<string, VarDecl>;

    constructor(private parentScope?: Scope) {
        this.definedVariables = new Map<string, VarDecl>();
    }

    public putVariable(variable: VarDecl) {
        this.definedVariables.set(variable.name.substr(1), variable);
    }

    public getVariable(name: string): Token | undefined {
        let retVar = this.definedVariables.get(name.substr(1));
        if (retVar) {
            return retVar.token;
        } else if (this.parentScope) {
            return this.parentScope.getVariable(name);
        }
        return undefined;
    }
}

export interface SemanticResult {
    errors: SemanticError[];
    correctPrograms: Map<string, ProgDecl>;
}

export class SemanticVerifier extends ASTVisitor<Scope, SemanticError[]> {
    // Global scope containing all defined programs with valid names
    private definedPrograms: Map<string, ProgDecl> = new Map<
        string,
        ProgDecl
    >();
    private correctPrograms: Map<string, ProgDecl> = new Map<
        string,
        ProgDecl
    >();

    constructor() {
        super();
    }

    public check(
        input: AST | AST[],
        context: Map<string, ProgDecl> | undefined,
    ): SemanticResult {
        let errors: SemanticError[] = [];

        if (context !== undefined) this.definedPrograms = context;

        let inputArr: AST[];

        if (input instanceof AST) {
            inputArr = [input];
        } else {
            inputArr = input;
        }

        inputArr.forEach(ast => {
            const astErrors = this.visit(ast, new Scope());
            errors = errors.concat(astErrors);
        });

        return { errors: errors, correctPrograms: this.correctPrograms };
    }

    protected defaultResult(): SemanticError[] {
        // Per default there are no semantic errors
        return [];
    }

    protected aggregateResult(
        aggregate: SemanticError[],
        nextResult: SemanticError[],
    ): SemanticError[] {
        // Concatenate lists such that all errors of all children are in the list
        return aggregate.concat(nextResult);
    }

    public visitProgDecl(ast: ProgDecl, arg: Scope): SemanticError[] {
        let errors: SemanticError[] = [];

        const scope = new Scope();
        ast.args.forEach(parameter => {
            if (scope.getVariable(parameter.name)) {
                errors.push(
                    new SemanticError(
                        SemanticErrorCause.DOUBLE_DECLARATION_PROGRAM_PARAMETER,
                        ast,
                        parameter.token,
                        {
                            parameter: this.getTokenErrorDisplay(
                                parameter.token,
                            ),
                        },
                    ),
                );
            } else {
                scope.putVariable(parameter);
            }
        });
        errors = errors.concat(this.visitChildren(ast, scope));

        if (errors.length === 0) {
            this.correctPrograms.set(ast.name, ast);
        }

        return errors;
    }

    public visitProgCallStmt(ast: ProgCallStmt, scope: Scope): SemanticError[] {
        let errors: SemanticError[] = [];

        const progCall = this.definedPrograms.get(ast.progName);
        if (progCall) {
            const requiredArgs = ast.getChildren().length;
            if (requiredArgs > progCall.args.length) {
                errors.push(
                    new SemanticError(
                        SemanticErrorCause.TOO_MANY_ARGUMENTS,
                        ast,
                        ast.token,
                        {
                            program: this.getTokenErrorDisplay(ast.token),
                            number_of_arguments: progCall.args.length.toString(),
                        },
                    ),
                );
            } else if (requiredArgs < progCall.args.length) {
                errors.push(
                    new SemanticError(
                        SemanticErrorCause.NOT_ENOUGH_ARGUMENTS,
                        ast,
                        ast.token,
                        {
                            program: this.getTokenErrorDisplay(ast.token),
                            number_of_arguments: progCall.args.length.toString(),
                        },
                    ),
                );
            }
        } else {
            errors.push(
                new SemanticError(
                    SemanticErrorCause.NO_SUCH_PROGRAM,
                    ast,
                    ast.token,
                    { program: this.getTokenErrorDisplay(ast.token) },
                ),
            );
        }
        errors = errors.concat(this.visitChildren(ast, scope));
        return errors;
    }

    public visitBuiltInCommand(
        ast: BuiltInCommand,
        scope: Scope,
    ): SemanticError[] {
        let errors: SemanticError[] = [];

        if (ast.requiredArgs > ast.getChildren().length) {
            errors.push(
                new SemanticError(
                    SemanticErrorCause.NOT_ENOUGH_ARGUMENTS,
                    ast,
                    ast.token,
                    {
                        program: this.getTokenErrorDisplay(ast.token),
                        number_of_arguments: ast.requiredArgs.toString(),
                    },
                ),
            );
        } else if (ast.requiredArgs < ast.getChildren().length) {
            errors.push(
                new SemanticError(
                    SemanticErrorCause.TOO_MANY_ARGUMENTS,
                    ast,
                    ast.token,
                    {
                        program: this.getTokenErrorDisplay(ast.token),
                        number_of_arguments: ast.requiredArgs.toString(),
                    },
                ),
            );
        }
        errors = errors.concat(this.visitChildren(ast, scope));
        return errors;
    }

    public visitSpecialBuiltInCommand(
        ast: SpecialBuiltInCommand,
        scope: Scope,
    ): SemanticError[] {
        let errors: SemanticError[] = [];

        if (ast.requiredArgs > ast.getChildren().length) {
            errors.push(
                new SemanticError(
                    SemanticErrorCause.NOT_ENOUGH_ARGUMENTS,
                    ast,
                    ast.token,
                    {
                        program: this.getTokenErrorDisplay(ast.token),
                        number_of_arguments: ast.requiredArgs.toString(),
                    },
                ),
            );
        } else if (ast.requiredArgs < ast.getChildren().length) {
            errors.push(
                new SemanticError(
                    SemanticErrorCause.TOO_MANY_ARGUMENTS,
                    ast,
                    ast.token,
                    {
                        program: this.getTokenErrorDisplay(ast.token),
                        number_of_arguments: ast.requiredArgs.toString(),
                    },
                ),
            );
        }
        errors = errors.concat(this.visitChildren(ast, scope));
        return errors;
    }

    public visitVarExpr(ast: VarExpr, scope: Scope): SemanticError[] {
        if (!scope.getVariable(ast.name)) {
            return [
                new SemanticError(
                    SemanticErrorCause.NO_SUCH_VARIABLE,
                    ast,
                    ast.token,
                    { variable: this.getTokenErrorDisplay(ast.token) },
                ),
            ];
        }
        return [];
    }

    public visitMakeStmt(ast: MakeStmt, scope: Scope): SemanticError[] {
        scope.putVariable(ast.declName);
        return this.visit(ast.value, scope);
    }

    public visitIfElseStmt(ast: IfElseStmt, scope: Scope): SemanticError[] {
        let errors: SemanticError[] = this.visit(ast.condition, scope);
        errors = errors.concat(this.visit(ast.thenBlock, new Scope(scope)));
        if (ast.elseBlock) {
            errors = errors.concat(this.visit(ast.elseBlock, new Scope(scope)));
        }
        return errors;
    }
    public visitRepeatStmt(ast: RepeatStmt, scope: Scope): SemanticError[] {
        let errors: SemanticError[] = this.visit(ast.repeatCount, scope);
        return errors.concat(this.visit(ast.loopBody, new Scope(scope)));
    }
    public visitWhileStmt(ast: WhileStmt, scope: Scope): SemanticError[] {
        let errors: SemanticError[] = this.visit(ast.condition, scope);
        return errors.concat(this.visit(ast.loopBody, new Scope(scope)));
    }
    // Code copied from https://github.com/tunnelvisionlabs/antlr4ts/blob/master/src/DefaultErrorStrategy.ts
    protected getTokenErrorDisplay(t: Token | undefined): string {
        if (!t) {
            return "<no token>";
        }
        let s = this.getSymbolText(t);
        if (!s) {
            if (this.getSymbolType(t) === Token.EOF) {
                s = "<EOF>";
            } else {
                s = `<${this.getSymbolType(t)}>`;
            }
        }
        return s;
    }

    protected getSymbolText(symbol: Token): string | undefined {
        return symbol.text;
    }

    protected getSymbolType(symbol: Token): number {
        return symbol.type;
    }
}



// WEBPACK FOOTER //
// ./src/app/xlogo-parser/semanticVerifier.ts